import { Link } from "react-router-dom";
import { MapPin, ChevronRight } from "lucide-react";
import { LOCATIONS } from "@/data/siteData";
import { CTABanner } from "@/components/CTABanner";
import { Badge } from "@/components/ui/badge";

export default function LocationsPage() {
  return (
    <div data-testid="locations-page">
      {/* Header */}
      <section className="py-12 sm:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3 animate-fade-in-up">
            Our Spaces
          </p>
          <h1
            data-testid="locations-heading"
            className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight mb-4 animate-fade-in-up animation-delay-100"
          >
            Coworking Centers in Pune
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto font-body leading-relaxed animate-fade-in-up animation-delay-200">
            5+ strategically located workspaces across Pune's most vibrant business corridors.
            Find the perfect space for your team.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
            {LOCATIONS.map((loc) => (
              <Link
                key={loc.id}
                to={`/locations/${loc.id}`}
                data-testid={`location-card-${loc.id}`}
                className="block bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] group"
              >
                <div className="aspect-[4/3] w-full overflow-hidden relative">
                  <img
                    src={loc.image}
                    alt={loc.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {loc.premium && (
                    <Badge className="absolute top-3 right-3 bg-sky-600 text-white border-0 text-xs">
                      Premium
                    </Badge>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-orange-500" />
                    <span className="text-xs uppercase tracking-[0.15em] font-medium text-slate-500">
                      {loc.tagline}
                    </span>
                  </div>
                  <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                    {loc.name}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">
                    {loc.shortDescription}
                  </p>
                  <span
                    data-testid={`location-view-${loc.id}`}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-500 group-hover:text-orange-600 transition-colors"
                  >
                    View Details
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTABanner
        title="Can't Decide? Let Us Help!"
        subtitle="Our team will help you find the perfect workspace based on your needs, team size, and budget."
        buttonText="Get a Call"
        buttonLink="/contact"
      />
    </div>
  );
}
